import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';

export interface Expense {
  id: string;
  date: string;
  category: string | null;
  supplierName: string | null;
  description: string | null;
  amount: string;
  igv: string;
  total: string;
}

export interface ExpenseCategory {
  id: string;
  name: string;
}

export interface ExpenseInput {
  categoryId?: string | null;
  supplierName?: string;
  supplierDoc?: string;
  docType?: string;
  docNumber?: string;
  amount: number;
  igv: number;
  accountId?: string | null;
  date: string;
  description?: string;
}

function errMsg(e: unknown, fallback: string): string {
  return (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? fallback;
}

export function useExpenses() {
  return useQuery({
    queryKey: ['expenses'],
    queryFn: async () => (await apiClient.get<{ items: Expense[]; total: number }>('/expenses')).data,
    retry: false,
  });
}

export function useCreateExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ExpenseInput) => (await apiClient.post('/expenses', input)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expenses'] });
      qc.invalidateQueries({ queryKey: ['treasury-accounts'] });
      toast.success('Gasto registrado');
    },
    onError: (e: unknown) => toast.error(errMsg(e, 'No se pudo registrar el gasto')),
  });
}

export function useExpenseCategories() {
  return useQuery({
    queryKey: ['expense-categories'],
    queryFn: async () => (await apiClient.get<ExpenseCategory[]>('/expenses/categories')).data,
    retry: false,
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => (await apiClient.post('/expenses/categories', { name })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expense-categories'] });
      toast.success('Categoría creada');
    },
    onError: (e: unknown) => toast.error(errMsg(e, 'No se pudo crear la categoría')),
  });
}
